import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";

/**
 * Lightweight markdown renderer — kept as a fallback for legacy pages whose
 * body_markdown is still in markdown form. New pages save HTML directly from
 * the TipTap WYSIWYG and skip this transform entirely.
 */
function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function inlineRules(s) {
  return s
    .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-[#0f0f0f] text-[#D4B07B] font-mono text-[13px]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-[#F4F1EA]">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A055] hover:text-[#D4B07B] underline underline-offset-2">$1</a>');
}

function renderMarkdown(src) {
  if (!src) return "";
  const lines = escapeHtml(src).split("\n");
  let html = "";
  let inList = null;
  const flushList = () => { if (inList) { html += `</${inList}>`; inList = null; } };
  for (let raw of lines) {
    const ln = raw.trimEnd();
    if (!ln.trim()) { flushList(); continue; }
    let m;
    if ((m = ln.match(/^###\s+(.+)/))) { flushList(); html += `<h3>${inlineRules(m[1])}</h3>`; continue; }
    if ((m = ln.match(/^##\s+(.+)/)))  { flushList(); html += `<h2>${inlineRules(m[1])}</h2>`; continue; }
    if ((m = ln.match(/^#\s+(.+)/)))   { flushList(); html += `<h1>${inlineRules(m[1])}</h1>`; continue; }
    if ((m = ln.match(/^>\s+(.+)/)))   { flushList(); html += `<blockquote>${inlineRules(m[1])}</blockquote>`; continue; }
    if ((m = ln.match(/^[-*]\s+(.+)/))) { if (inList !== "ul") { flushList(); html += '<ul>'; inList = "ul"; } html += `<li>${inlineRules(m[1])}</li>`; continue; }
    if ((m = ln.match(/^\d+\.\s+(.+)/))) { if (inList !== "ol") { flushList(); html += '<ol>'; inList = "ol"; } html += `<li>${inlineRules(m[1])}</li>`; continue; }
    flushList();
    html += `<p>${inlineRules(ln)}</p>`;
  }
  flushList();
  return html;
}

/** If body looks like HTML (has a <tag>), use it as-is. Otherwise treat as markdown. */
function bodyToHtml(body) {
  if (!body) return "";
  if (/<\/?(p|h[1-6]|ul|ol|li|blockquote|strong|em|a|img|br)\b/i.test(body)) return body;
  return renderMarkdown(body);
}

export default function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setPage(null); setErr(false);
    http.get(`/cms/pages/${slug}`).then(({ data }) => setPage(data)).catch(() => setErr(true));
    return () => { document.title = "Simple Truths Club"; };
  }, [slug]);

  useEffect(() => {
    if (page) document.title = `${page.seo_title || page.title} · Simple Truths Club`;
  }, [page]);

  if (err) return (
    <Layout>
      <section className="bg-black py-24 text-center px-6" data-testid="cms-page-404">
        <h1 className="font-display text-4xl text-[#F4F1EA] tracking-wide mb-4">Page not found</h1>
        <p className="font-body text-[#8a857d] mb-6">This page hasn't been published yet.</p>
        <Link className="btn-copper inline-flex" to="/">← Back to home</Link>
      </section>
    </Layout>
  );

  if (!page) return <Layout><div className="py-24 text-center font-ui text-[#737373] tracking-[0.3em] text-xs">LOADING…</div></Layout>;

  return (
    <Layout>
      <section className="bg-black pt-14 md:pt-20 pb-24" data-testid="cms-page">
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">Simple Truths Club</div>
          <h1 className="font-display text-5xl md:text-7xl text-[#F4F1EA] tracking-tight leading-[1.05] mb-4" data-testid="cms-page-title">
            {(page.title || "").toUpperCase()}
          </h1>
          {page.subtitle && (
            <p className="font-script text-2xl md:text-3xl text-[#D4B07B] mb-10" data-testid="cms-page-subtitle">{page.subtitle}</p>
          )}
          <div className="copper-divider my-8 mx-auto" />
          <div
            className="cms-body"
            data-testid="cms-page-body"
            dangerouslySetInnerHTML={{ __html: bodyToHtml(page.body_markdown || "") }}
          />
        </div>
      </section>
    </Layout>
  );
}
